export const DEFAULT_CHECKOUT_TRUCK_CAPACITY = 500;

import { isUnknownRecord } from "../validation/narrow";

const STANDARD_TRUCK_TYPES = [
  "dryVan",
  "flatbed",
  "stepDeck",
  "boxTruck",
  "dropTrailer",
  "pickupTruck",
] as const;

export interface ResolvedTruckCapacity {
  capacity: number;
  truckType: string;
}

export function resolvePrimaryTruckCapacity(
  capacities: unknown,
  defaults?: unknown,
): ResolvedTruckCapacity | null {
  const resolved = resolveTruckCapacities(capacities, defaults);
  return resolved.reduce<ResolvedTruckCapacity | null>(
    (largest, current) =>
      !largest || current.capacity > largest.capacity ? current : largest,
    null,
  );
}

export function resolveCheckoutTruckOptions(
  capacities: unknown,
  defaults?: unknown,
): ResolvedTruckCapacity[] {
  const resolved = isUnknownRecord(capacities)
    ? readStandardCapacities(capacities)
    : readDefaultCapacities(defaults);
  if (
    !isUnknownRecord(capacities) &&
    !resolved.length &&
    !isUnknownRecord(defaults)
  )
    return [{ truckType: "dryVan", capacity: DEFAULT_CHECKOUT_TRUCK_CAPACITY }];
  return resolved.filter(
    (option) => option.truckType === "dryVan" || option.truckType === "flatbed",
  );
}

export function resolveSelectedTruckCapacity(
  capacities: unknown,
  defaults?: unknown,
  truckType?: string,
  quantity?: number,
): ResolvedTruckCapacity | null {
  const options = resolveCheckoutTruckOptions(capacities, defaults);
  return truckType
    ? (options.find((option) => option.truckType === truckType) ?? null)
    : (options.find(
        (option) => quantity === undefined || option.capacity >= quantity,
      ) ?? null);
}

export function resolveTruckCapacities(
  capacities: unknown,
  defaults?: unknown,
): ResolvedTruckCapacity[] {
  return isUnknownRecord(capacities)
    ? readConfiguredCapacities(capacities)
    : readDefaultCapacities(defaults);
}

function readConfiguredCapacities(
  input: Record<string, unknown>,
): ResolvedTruckCapacity[] {
  const standard = readStandardCapacities(input);
  const custom = Array.isArray(input.custom)
    ? input.custom.flatMap((config) => {
        if (!isUnknownRecord(config) || config.enabled !== true) return [];
        const truckType =
          typeof config.truckTypeId === "string" && config.truckTypeId
            ? config.truckTypeId
            : "custom";
        return toCapacity(config.units, truckType);
      })
    : [];
  return [...standard, ...custom];
}

function readStandardCapacities(
  input: Record<string, unknown>,
): ResolvedTruckCapacity[] {
  return STANDARD_TRUCK_TYPES.flatMap((truckType) => {
    const config = input[truckType];
    if (!isUnknownRecord(config) || config.enabled !== true) return [];
    return toCapacity(config.units, truckType);
  });
}

function readDefaultCapacities(input: unknown): ResolvedTruckCapacity[] {
  if (!isUnknownRecord(input)) return [];
  return STANDARD_TRUCK_TYPES.flatMap((truckType) =>
    toCapacity(input[truckType], truckType),
  );
}

function toCapacity(
  units: unknown,
  truckType: string,
): ResolvedTruckCapacity[] {
  if (typeof units !== "number" || !Number.isSafeInteger(units) || units < 1)
    return [];
  return [{ capacity: units, truckType }];
}
